import { GoogleLogin, useGoogleOneTapLogin } from "@react-oauth/google";
import React from "react";
import { useCookies } from "react-cookie";
import { decodeToken } from "react-jwt";
import { toast } from "sonner";
import { queryClient } from "../../../../App";
import SearchableSelect from "../../../components/SearchableSelect";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { getJobFunctions } from "../../../seed/getMetadata";
import { API, applyAuthorizationHeader } from "../../../services/apiService";
import { setAuthorizationToken } from "../../../services/auth/authToken";

function GoogleAppSignin() {
	const [newAccount, setNewAccount] = React.useState(false);
	const [_, _setCookie] = useCookies(["Authorization"]);

	const [accountData, setAccountData] = React.useState({
		jobTitle: "",
		areasOfExpertise: "",
		email: "",
		firstName: "",
		lastName: "",
		picture: "",
		redirect: "",
		authorizationToken: "",
	});

	function getUserAccount(redirectUrl, authorizationToken) {
		API.get("users/account", { headers: { Authorization: authorizationToken } })
			.then((res) => {
				if (res.data === "") {
					setNewAccount(true);
				} else {
					window.location.href = redirectUrl;
				}
			})
			.catch(() => {
				toast.error("Failed to fetch user account");
			});
	}

	function onGoogleSignIn(googleUser) {
		if (googleUser.credential) {
			const { email, given_name: firstName, family_name: lastName, picture } = decodeToken(googleUser.credential);
			const credential = `GO::${googleUser.credential}`;
			const redirectUrl = `dproz://redirect?token=${encodeURIComponent(credential)}`;

			const expireDate = new Date();
			expireDate.setDate(expireDate.getDate() + 30);
			queryClient.removeQueries({ queryKey: ["user", "account"], exact: true });
			setAuthorizationToken(credential, expireDate);
			applyAuthorizationHeader(credential);

			setAccountData({
				jobTitle: "",
				areasOfExpertise: "",
				email,
				firstName,
				lastName,
				picture,
				redirect: redirectUrl,
				authorizationToken: credential,
			});

			getUserAccount(redirectUrl, credential);
		}
	}

	useGoogleOneTapLogin({
		onSuccess: onGoogleSignIn,
		auto_select: true,
	});

	function createNewAccount(e) {
		e.preventDefault();
		const data = {
			emailAddress: accountData.email,
			firstName: accountData.firstName,
			middleName: "",
			lastName: accountData.lastName,
			phoneNumber: "",
			jobPreferences: {
				areasOfExpertise: [accountData.areasOfExpertise],
				openForWorkTypes: [],
				recentGraduate: null,
				graduationDate: null,
				professionalSince: null,
				locationPreferenceType: "Open to Relocate Anywhere",
				locationsPreferred: [],
				desiredTitle: accountData.jobTitle,
			},
		};

		API.post("users", data, { headers: { Authorization: accountData.authorizationToken } })
			.then(() => {
				window.location.href = accountData.redirect;
			})
			.catch((error) => {
				if (error.response && error.response.status === 400) {
					toast.error("Email already exists. Please sign in.");
				} else {
					toast.error("Failed to create account. Please try again.");
				}
			});
	}

	return (
		<div className="min-h-screen flex flex-col items-center justify-center">
			<img
				src="/images/logo.png"
				alt="DProz Logo"
				className="p-2 mb-4 mx-auto"
				style={{
					width: "180px",
				}}
			/>
			<div className="mb-8 text-center space-y-3">
				<h3 className="font-bold font-plus-jakarta text-xl">
					{newAccount ? `Hello, ${accountData.firstName}` : "Your dream job is just a click away!"}
				</h3>
				<p className="text-[#5C5C5C] text-base font-inter">
					{newAccount
						? "Looks like you are new here. Please set up your account to get started."
						: "Click the button below to sign in with your Google account and start exploring job opportunities."}
				</p>
			</div>
			<div className="flex items-center justify-center">
				{newAccount ? (
					<form onSubmit={createNewAccount} className="max-w-[90vw] w-[600px]">
						<div className="flex flex-col gap-1 mb-4">
							<label className="font-semibold text-sm text-gray-700" htmlFor="">
								Job Title
							</label>
							<Input
								type="text"
								className="border border-gray-200 h-9 text-sm placeholder:text-sm rounded px-2 py-1 placeholder:text-[#7A7A7A]"
								placeholder="Enter your job title"
								value={accountData.jobTitle}
								onChange={(e) => setAccountData({ ...accountData, jobTitle: e.target.value })}
								name="jobTitle"
							/>
						</div>
						<div className="flex flex-col gap-1 mb-6">
							<label className="font-semibold text-sm text-gray-700" htmlFor="">
								What’s your superpower skill or area of expertise?
							</label>
							<SearchableSelect
								placeholder="Select all that apply"
								options={[...getJobFunctions()].sort()}
								onChange={(val) => {
									setAccountData({
										...accountData,
										areasOfExpertise: val,
									});
								}}
								value={accountData.areasOfExpertise}
							/>
						</div>
						<Button type="submit" variant="default" className="w-full" onClick={createNewAccount}>
							Create Account
						</Button>
					</form>
				) : (
					<GoogleLogin
						onSuccess={onGoogleSignIn}
						onFailure={() => toast.error("Login Failed")}
						size="large"
						text="continue_with"
					/>
				)}
			</div>
		</div>
	);
}

export default GoogleAppSignin;
